<template>
    <ion-page>
        <standard-header title="Checks" align="middle"/>
        <ion-content :fullscreen="true" v-if="shipment.id">
            <pull-down-refresh :callback="refresh"/>
            <ion-segment v-model="segment">
                <ion-segment-button value="items">
                    <ion-label>
                        {{ shipment.reference }}
                        <ion-badge size="large" color="danger">{{ shipment.lines.length }}</ion-badge>
                    </ion-label>
                </ion-segment-button>
                <ion-segment-button value="boxes" :disabled="shipment.is_list">
                    <ion-label>Boxes</ion-label>
                </ion-segment-button>
            </ion-segment>
            <ion-item color="tertiary">
                <ion-label><strong>Pickers</strong></ion-label>
                <p><span v-for="user in shipment.pickers" :key="user.id">{{ user.initials }}&nbsp;</span></p>
            </ion-item>
            <ion-searchbar animated mode="ios" v-model="filter" @keyup.enter="refresh()"/>
            <ion-list v-show="segment === 'items'">
                <ion-item v-if="lines.length <1" button detail
                          @click="$router.replace({ name: 'checks.mine', direction: 'back'})">
                    <ion-label>No more lines to check on this shipment</ion-label>
                </ion-item>
                <ion-item v-for="line in lines" :key="line.id" button detail @click="check(line)">
                    <ion-icon color="danger" class="has-checker-errors" v-if="hasAnyErrors(line)" slot="start" :icon="warningOutline"/>
                    <ion-icon class="child-line-icon" slot="start" :icon="returnDownForwardOutline" v-if="line.is_child" />
                    <ion-label>
                        <h2>
                            <strong v-if="line.is_parent && !line.checked_at">?</strong>
                            <strong v-else>{{ line.qty_checked ? line.qty_checked : line.qty_picked }}</strong> x
                            {{ line.item.name }}
                        </h2>
                        <h3>
                            {{ line.item.item_number }} / {{ line.sku.sku }}
                        </h3>
                    </ion-label>
                    <small class="ion-float-end">
                        <span v-if="shipment.is_list || line.is_child">
                            Checked:<br /> {{ line.checking.qty_checked }} / {{ line.qty_required }}
                        </span>
                        <span v-else>
                            Boxed:<br/>
                            <ion-text color="success">
                                {{ line.checking.qty_boxed }}
                            </ion-text>
                            <span v-if="line.checking.qty_unboxed > 0"> / </span>
                            <ion-text v-if="line.checking.qty_unboxed > 0" color="danger">
                                {{ line.checking.qty_authoritative }}
                            </ion-text>
                        </span>
                    </small>
                </ion-item>
            </ion-list>
            <div>
                <div v-show="segment === 'boxes'">
                    <ion-item>
                        <ion-label>Shipping Method</ion-label>
                        <ion-select :value="this.method" @ionChange="changeShipmentMethod($event)">
                            <ion-select-option value="UPS">Boxes</ion-select-option>
                            <ion-select-option value="HMT">Pallets</ion-select-option>
                        </ion-select>
                    </ion-item>

                    <box-manifest-table
                        :current-shipment-split="currentShipmentSplit"
                        :shipment="shipment"
                        :filter="filter"
                        @updated="refresh()"
                    />

                    <ion-item v-if="emptyBoxNumbers.length > 0">
                        <ion-label class="ion-text-wrap">
                            <h2>Empty Boxes</h2>
                            <p>These empty boxes will prevent you from signing off:</p>
                            <p>{{ emptyBoxNumbers.join(', ') }}</p>
                        </ion-label>
                    </ion-item>
                </div>
            </div>
        </ion-content>
        <ion-footer>
            <ion-row>
                <ion-col size="4">
                    <ion-button expand="block" size="medium" fill="outline" color="primary" @click="switchShipment()" v-if="currentShipmentSplit">
                        Shipment {{ currentShipmentSplit.reference }}
                    </ion-button>
                </ion-col>
                <ion-col size="4">
                    <ion-button v-if="segment === 'items'" expand="block" size="medium" fill="outline" color="primary"
                                @click="splitShipment()">
                        Split
                    </ion-button>
                    <ion-button v-if="segment === 'boxes'" expand="block" size="medium" fill="outline" color="danger"
                                @click="clearBoxPlan()">
                        Clear All
                    </ion-button>
                </ion-col>
                <ion-col size="4">
                    <ion-button v-if="segment === 'items' && lines && lines.length > 0 && allLinesChecked " expand="block" size="medium" fill="outline" color="success" @click="signOff()" :disabled="!(allItemsBoxed && allLinesChecked)">
                        <ion-icon slot="icon-only" :icon="checkmarkDoneCircleOutline"/>
                        <span v-if="!shipment.is_list && unboxedItemsQty > 0">{{ unboxedItemsQty }} left</span>
                    </ion-button>
                    <ion-button v-if="segment === 'boxes'" expand="block" size="medium" fill="outline" color="warning" @click="deleteBox()">
                        Del. Box
                    </ion-button>
                </ion-col>
            </ion-row>
        </ion-footer>
    </ion-page>
</template>

<script>
import PullDownRefresh from "@/components/PullDownRefresh";
import {
    IonContent, IonItem, IonLabel, IonList, IonPage, IonSegment, IonRow, IonCol,
    IonSegmentButton, IonBadge, IonText, IonFooter, IonButton, IonSearchbar,
    alertController, pickerController, IonIcon, IonSelect, IonSelectOption
} from '@ionic/vue';
import {modalController} from '@ionic/vue';
import StandardHeader from "@/components/StandardHeader";
import EventBus from "@/EventBus";
import _, {map} from 'lodash';
import {checkmarkDoneCircleOutline, returnDownForwardOutline, warningOutline} from 'ionicons/icons';
import CheckQuantityModal from "@/views/modules/checking/pages/check/modals/CheckQuantityModal";
import BoxManifestTable from "@/views/modules/checking/pages/check/components/BoxManifestTable";

export default {
    name: "ListItems",
    inject: ['$http', '$toast', '$validationAlert'],
    components: {
        BoxManifestTable,
        StandardHeader, PullDownRefresh,
        IonContent, IonItem, IonLabel, IonList, IonPage, IonSegment, IonSegmentButton,
        IonBadge, IonText, IonFooter, IonButton, IonSelect, IonSelectOption,
        IonRow, IonCol, IonIcon, IonSearchbar
    },
    computed: {
        params() {
            return this.$route.params;
        },
        highestPalletNumber() {
            let highestPalletNumber = 0;
            _.each(this.shipment.boxes, box => {
                // if we've got a split, then box numbers
                // from a different split shouldn't count towards
                // your current split...
                if (this.currentShipmentSplit && this.currentShipmentSplit.id !== box.shipment_split_id) {
                    return;
                }

                if (box.pallet > highestPalletNumber) {
                    highestPalletNumber = box.pallet;
                }
            });
            return highestPalletNumber;
        },
        highestBoxNumber() {
            let highestBoxNumber = 0;
            _.each(this.shipment.boxes, box => {
                // if we've got a split, then box numbers
                // from a different split shouldn't count towards
                // your current split...
                if (this.currentShipmentSplit && this.currentShipmentSplit.id !== box.shipment_split_id) {
                    return;
                }

                if (box.number > highestBoxNumber) {
                    highestBoxNumber = box.number;
                }
            });
            return highestBoxNumber;
        },
        emptyBoxNumbers() {
            return _.chain(this.shipment.boxes).filter(box => {
                return box.total_item_count < 1;
            }).map('number').value();
        },
        method() {
            if (this.hasSplits && this.currentShipmentSplit) {
                return this.currentShipmentSplit.method;
            }
            return this.shipment.method;
        },
        hasSplits() {
            return this.shipment.splits && this.shipment.splits.length;
        },
        include() {
            return 'boxes,boxes.total_item_count,checkers,pickers,lines,lines.errors,lines.item,lines.sku,lines.sku.item,lines.sku.image_urls,lines.location,lines.item.mpns,lines.item.image_urls,lines.item.skus,lines.checking,lines.picker,splits'
        },
        lines() {
            if (this.filter.length < 1) {
                return this.shipment.lines;
            }
            // cheap search counts number of string matches and includes in results
            return _.filter(this.shipment.lines, line => {
                const data = [
                    line.sku.barcode,
                    line.sku.sku,
                    line.sku.name,
                    line.item.item_number,
                    ..._.map(line.item.mpns,'mpn')
                ];
                const json = JSON.stringify(data);
                return json && json.toLowerCase().indexOf(this.filter.toLowerCase()) > -1;
            });
        },
        unboxedItemsQty() {
            return _.sum(_.map(this.shipment.lines, line => {
                if (line.is_child) {
                    return 0;
                }
                return line.checking.qty_unboxed;
            }));
        },
        allItemsBoxed() {
            // list items aren't boxed, always assume they're boxed
            if (this.shipment.is_list) {
                return true;
            }
            return this.unboxedItemsQty < 1;
        },
        allLinesChecked() {
            return _.filter(this.shipment.lines, line => {
                return !line.checked_at && !line.is_child;
            }).length < 1;
        }
    },
    methods: {
        hasAnyErrors(line) {
            return this.checkerErrors(line).length + this.pickerErrors(line).length > 0;
        },
        checkerErrors(line) {
            return _.filter(line.errors, error => {
                return error.code.group.code === 'CHECK_ERRORS';
            });
        },
        pickerErrors(line) {
            return _.filter(line.errors, error => {
                return error.code.group.code === 'PICK_ERRORS';
            });
        },
        ionViewDidEnter() {
            this.refresh();
        },
        deleteBox() {

            const additionalMessage = this.currentShipmentSplit?' from Shipment ' + this.currentShipmentSplit.reference:'';
            const message = 'Please enter the box number (or numbers, separated by a comma) you want to delete' + additionalMessage;
            alertController.create({
                header: 'Delete Box',
                message: message,
                inputs: [{
                    label: 'Box Number',
                    name: 'box'
                }],
                buttons: [
                    'Cancel', {
                    text: 'Ok',
                    handler: (values) => {
                        EventBus().emitter.emit('loading', true);
                        return this.$http.delete(`/api/checks/shipments/${this.shipment.id}/boxes/${values.box}`, {params:{
                            by_number: 1,
                            split_id: this.currentShipmentSplit?this.currentShipmentSplit.id:null
                        }}).then(() => {
                            this.$emit('updated');
                            this.refresh();
                            this.$toast.success("Boxes deleted");
                        }).catch(error => {
                            if (error && error.response.status === 404) {
                                this.$validationAlert('Box number not found', 'Not Found');
                                return;
                            }
                            this.$validationAlert(error);
                        }).finally(() => {
                            EventBus().emitter.emit('loading', false);
                        });
                    }
                }]
            }).then(alert => alert.present());

        },
        changeShipmentMethod(event) {

            let url = `/api/shipments/${this.shipment.id}/method`;
            if (this.currentShipmentSplit) {
                url = `/api/shipments/${this.shipment.id}/split-boxes/${this.currentShipmentSplit.id}`;
            }
            EventBus().emitter.emit('loading', true);
            this.$http.put(url, {method: event.detail.value}, {params: {include: this.include}}).then(response => {
                this.shipment = response.data;
                if (this.currentShipmentSplit) {
                    this.currentShipmentSplit.method = event.detail.value;
                }
            }).catch(error => {
                this.$validationAlert(error);
            }).finally(() => {
                EventBus().emitter.emit('loading', false);
            });
        },
        /**
         * Open the CheckLine modal, but surround it with the
         * NavigableModalBase modal component (which acts as a nav)
         * @param line
         */
        check(line) {
            modalController.create({
                component: CheckQuantityModal,
                componentProps: {
                    highestBoxNumber: this.highestBoxNumber,
                    highestPalletNumber: this.highestPalletNumber,
                    currentShipmentSplit: this.currentShipmentSplit,
                    shipment: this.shipment,
                    line: line
                }
            }).then(modal => {
                modal.present();
                modal.onDidDismiss().then(changed => {
                    if (changed.data) {
                        this.refresh();
                    }
                })
            });
        },
        async signOff() {
            alertController.create({
                header: 'Checking Sign Off',
                message: 'Are you sure you want to sign this shipment',
                buttons: [
                    'Cancel',
                    {
                        text: 'Confirm',
                        handler: () => {
                            EventBus().emitter.emit('loading', true);
                            this.$http
                                .post(`/api/checks/shipments/${this.shipment.id}/signoff`)
                                .then(() => {
                                    this.$toast.success("Shipment completed and signed off");
                                    this.$router.replace({name: 'checks.all'});
                                }).catch(error => {
                                    this.$validationAlert(error);
                                }).finally(() => {
                                    EventBus().emitter.emit('loading', false);
                                });
                        }
                    }]
            }).then(alert => alert.present());
        },
        async switchShipment() {
            let selectedIndex = null;
            const picker = await pickerController.create({
                buttons: [{text: 'Cancel', role: 'cancel'}, {text: 'Confirm'}],
                columns: [{
                    name: 'split',
                    options: map(this.shipment.splits, (split, i) => {
                        // track which is the currently selected shipment so when we
                        // call present(), we can pre-select the correct one...
                        if (this.currentShipmentSplit && split.id === this.currentShipmentSplit.id) {
                            selectedIndex = i;
                        }
                        return {
                            value: split,
                            text: 'Shipment ' + split.reference
                        }
                    })
                }]
            });
            // pre select the current shipment for UI benefit
            if (selectedIndex) {
                picker.columns[0].selectedIndex = selectedIndex;
            }
            await picker.present();
            picker.onDidDismiss().then(value => {
                if (value && value.data) {
                    this.currentShipmentSplit = value.data.split.value;
                }
            });
        },
        splitShipment() {
            alertController.create({
                header: 'Are you sure?',
                message: 'Are you sure you want to split this shipment into multiple consignments?',
                buttons: [{
                    role: 'cancel',
                    text: 'Cancel'
                }, {
                    text: 'Split',
                    handler: () => {
                        EventBus().emitter.emit('loading', true);
                        this.$http.post(`/api/shipments/${this.shipment.id}/split-boxes`).then(() => {
                            this.refresh();
                        }).catch(error => {
                            this.$validationAlert(error);
                        }).finally(() => {
                            EventBus().emitter.emit('loading', false);
                        })
                    }
                }]
            }).then(alert => {
                alert.present();
            });
        },
        clearBoxPlan() {
            alertController.create({
                header: 'Are you sure?',
                message: 'Are you sure you want to delete this shipment\'s box plan?',
                buttons: [{
                    role: 'cancel',
                    text: 'Cancel'
                }, {
                    text: 'Delete',
                    handler: () => {
                        this.$http.delete(`/api/checks/shipments/${this.params.shipment}`, {
                            params: {include: this.include}
                        })
                            .then(() => {
                                this.refresh();
                            }).catch(error => {
                            this.$validationAlert(error);
                        });
                    }
                }]
            }).then(alert => {
                alert.present();
            });
        },
        /**
         * Refresh the list of items on this shipment. We're also going to do a sneaky
         * refresh on the box plan, but that won't trigger the loading spinner or the
         * pull down refresh ...
         * @param event
         */
        refresh(event = null) {
            EventBus().emitter.emit('loading', true);
            this.$http.get(`/api/checks/shipments/${this.params.shipment}`, {
                params: {include: this.include}
            }).then(response => {
                this.shipment = response.data;

                if (this.hasSplits) {
                    if (!this.currentShipmentSplit) {
                        this.currentShipmentSplit = this.shipment.splits[0];
                    }
                } else {
                    this.currentShipmentSplit = null;
                }
            }).finally(() => {
                if (event && event.target.complete) {
                    event.target.complete();
                }
                EventBus().emitter.emit('loading', false);
            });
        }
    },
    data() {
        return {
            filter: '',
            currentShipmentSplit: null,
            segment: 'items',
            shipment: {}
        }
    },
    setup() {
        return {
            checkmarkDoneCircleOutline,
            warningOutline,
            returnDownForwardOutline
        };
    }
}
</script>

<style scoped>
.has-checker-errors {
    margin-right: 0.5em;
    left: -0.25em;
    position:relative;
}

.child-line-icon {
    margin-right: 10px;
}

ion-badge {
    position: relative;
    top: 5px;
}

ion-label.box-number {
    width: 10%;
}
</style>
